/**
 * Registry that bridges the in-component fallback timeout (kept in a useRef on
 * ViewProperty) and the global socket sagas. When the async Minimum Rent recalc
 * notification arrives, the saga calls clearMinimumRentFallback(propertyId) so
 * the neutral "timeout" toast is never shown for an update that already
 * completed.
 *
 * Each entry also stores the correlationId that was generated for the PUT that
 * started the in-progress toast. The sagas compare the correlationId echoed on
 * the socket notification against this stored value so a stray notification
 * (e.g. a concurrent save on the same property, or a save from another tab)
 * cannot flip the wrong toast.
 */
const fallbacks = new Map();

export const registerMinimumRentFallback = (
  propertyId: string,
  cancel: Function,
  correlationId: ?string,
) => {
  // Defense in depth: if a fallback is already registered for this property
  // (e.g. a rapid second save before the first one resolved), cancel the
  // previous one so its timer/callback cannot fire after being superseded.
  const existing = fallbacks.get(propertyId);
  if (existing && typeof existing.cancel === 'function') {
    existing.cancel();
  }
  fallbacks.set(propertyId, { cancel, correlationId });
};

export const getMinimumRentCorrelationId = (propertyId: string): ?string => {
  const entry = fallbacks.get(propertyId);
  return entry ? entry.correlationId : undefined;
};

export const clearMinimumRentFallback = (propertyId: string) => {
  const entry = fallbacks.get(propertyId);
  if (entry) {
    if (typeof entry.cancel === 'function') {
      entry.cancel();
    }
    fallbacks.delete(propertyId);
  }
};
